import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { getPresentOptions } from '@/services/quoteIngest.service.js';
import { optionRowLabel, deductibleLine } from '@/lib/quoteFields.js';
import { formatCurrency } from '@/lib/format.js';
import { Copy, Check, Eye } from 'lucide-react';

// WHAT THE CLIENT IS LOOKING AT, WITHOUT A CLICK (CC PROMPT 31 Part C5).
//
// David: "you have to click 'Present' to bring this up". A modal is a fine
// EDITOR and a bad status display. PresentQuotesSheet stays the editor; this
// answers the question the sheet was being opened to answer.
//
// READ-ONLY BY CONSTRUCTION. Nothing here writes, and there is deliberately no
// checkbox, no recommend control and no send. The only actions are "open the
// editor" and "copy the link", so the strip can never become a second way to
// present and therefore can never disagree with the sheet.
//
// It renders NOTHING until something has actually been presented. An empty
// strip on every unpresented deal would be noise on the surface David reads
// most, and absence already says "nothing has gone out".
export default function PresentedStrip({ dealId, refreshKey, onOpenEditor }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!dealId) return undefined;
    let dead = false;
    getPresentOptions(dealId)
      .then((d) => { if (!dead) setData(d); })
      .catch(() => { if (!dead) setData(null); });
    return () => { dead = true; };
  }, [dealId, refreshKey]);

  // The presented SET, derived from the same payload the sheet reads. One fact,
  // one derivation: the strip does not decide what is presented, it reports it.
  const presented = useMemo(() => {
    const rows = [];
    for (const q of (data?.quotes || [])) {
      for (const o of (q.options || [])) {
        if (o.presented === true) rows.push({ q, o });
      }
    }
    return rows;
  }, [data]);

  const towerUrl = data?.tower_url || '';
  const copy = useCallback(() => {
    if (!towerUrl) return;
    navigator.clipboard.writeText(towerUrl)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error('Could not copy the Tower link'));
  }, [towerUrl]);

  if (!presented.length) return null;

  return (
    <div className="rounded-card border border-border bg-surface px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          Presented
        </span>
        <span className="text-xs text-text-dim">
          {presented.length === 1 ? '1 option' : `${presented.length} options`} in front of the client
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {towerUrl ? (
            <Button type="button" variant="ghost" size="sm" onClick={copy} className="gap-1 h-7 text-xs">
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied' : 'Tower link'}
            </Button>
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={onOpenEditor} className="gap-1 h-7 text-xs">
            <Eye size={13} /> Change
          </Button>
        </div>
      </div>

      <ul className="mt-2 space-y-1">
        {presented.map(({ q, o }) => (
          <li key={o.id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
            {/* carrier_display, not quotes.carrier: the raw column holds a slug
                on a large share of rows and a slug must never reach a surface. */}
            <span className="font-semibold text-text">{q.carrier_display || q.carrier || 'Unknown carrier'}</span>
            {optionRowLabel(o.option_label, o.coverage_type)
              ? <span className="text-text-muted">{optionRowLabel(o.option_label, o.coverage_type)}</span>
              : null}
            {o.coverage_type ? <span className="text-text-muted">{o.coverage_type}</span> : null}
            <span className="text-text-dim">{deductibleLine(o)}</span>
            {o.broker_recommended ? (
              <Badge variant="outline" className="text-[9px]">Recommended</Badge>
            ) : null}
            <span className="ml-auto font-semibold text-text">
              {o.total_premium != null ? formatCurrency(o.total_premium) : 'no premium'}
            </span>
          </li>
        ))}
      </ul>
      {/* Premium is strictly pre-tax everywhere in TRACON; the payment step adds
          taxes and the platform fee. Said once here so the strip cannot be read
          as a quote for what the client will be charged. */}
      <p className="mt-1.5 text-[10px] text-text-dim">Premium shown pre-tax.</p>
    </div>
  );
}
